// src/pages/PrivacyPolicy.jsx

import {
  Box,
  Container,
  Heading,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../components/PublicLayout";

const BRAND_RED = "#E63946";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

function Section({ number, title, children }) {
  return (
    <Box
      width="100%"
      p={{ base: 5, md: 6 }}
      bg="white"
      borderWidth="1px"
      borderColor={BRAND_BORDER}
      borderRadius="xl"
      boxShadow="sm"
    >
      <Text
        mb={2}
        color={BRAND_RED}
        fontSize="xs"
        fontWeight="extrabold"
        letterSpacing="wide"
      >
        {number}
      </Text>
      <Heading as="h2" size="md" mb={3} color="gray.900">
        {title}
      </Heading>
      <Box color="gray.700" lineHeight="1.8">
        {children}
      </Box>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <VStack align="stretch" spacing={2} mt={3} pl={2}>
      {items.map((item) => (
        <Text key={item}>• {item}</Text>
      ))}
    </VStack>
  );
}

export default function PrivacyPolicy() {
  return (
    <PublicLayout>
      <Helmet>
        <title>Privacy Policy | Postcode.Coupons</title>
        <meta
          name="description"
          content="How Postcode.Coupons collects, uses, stores and protects personal information."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box bg={BRAND_RED_LIGHT} py={{ base: 8, md: 14 }} minH="100vh">
        <Container maxW="container.md">
          <Box mb={{ base: 7, md: 10 }}>
            <Text
              color={BRAND_RED}
              fontSize="xs"
              fontWeight="extrabold"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Postcode.Coupons
            </Text>
            <Heading as="h1" mt={2} color="gray.900" size={{ base: "xl", md: "2xl" }}>
              Privacy Policy
            </Heading>
            <Text mt={3} color="gray.600">
              Last updated: 4 October 2026
            </Text>
            <Text mt={4} color="gray.700" lineHeight="1.8">
              This policy explains how Postcode.Coupons collects and uses personal
              information when people browse coupons, create accounts, place coupons,
              make bookings, use QR features or otherwise use the platform.
            </Text>
          </Box>

          <VStack align="stretch" spacing={5}>
            <Section number="01" title="Who we are">
              <Text>
                Postcode.Coupons is a postcode-sector coupon platform. Businesses
                place coupons in postcode sectors and users can browse, scan and
                redeem coupons through the platform.
              </Text>
              <Text mt={3}>
                For privacy enquiries, contact{" "}
                <Link href="mailto:support@postcode.coupons" color={BRAND_RED} fontWeight="bold">
                  support@postcode.coupons
                </Link>.
              </Text>
            </Section>

            <Section number="02" title="Information we collect">
              <BulletList
                items={[
                  "Account information, including name, email address and authentication identifiers.",
                  "Business information, including business name, contact details, postcode, logo and profile information.",
                  "Booking, coupon-placement, campaign, credit and invoice information.",
                  "Coupon content, QR codes, scans, claims and redemption activity.",
                  "Postcode sectors, map selections, search entries and saved preferences.",
                  "Referral information where a referral link or code is used.",
                  "Payment status and transaction references. Card details are handled by the payment provider and are not stored by Postcode.Coupons.",
                  "Technical and security information, including IP address, browser, device, timestamps, logs and error information.",
                  "Messages and support requests sent to us.",
                ]}
              />
            </Section>

            <Section number="03" title="How we use information">
              <BulletList
                items={[
                  "Create and secure accounts and provide access to the business cockpit.",
                  "Process coupon placements, bookings, payments, credits and invoices.",
                  "Operate postcode maps, coupon pages, QR scans, claims and redemption features.",
                  "Prepare campaigns for printing, dispatch and Royal Mail delivery.",
                  "Provide service, security and account communications.",
                  "Prevent fraud, misuse, unauthorised access and other harmful activity.",
                  "Maintain, troubleshoot and improve platform reliability and performance.",
                  "Comply with legal, tax, accounting and regulatory obligations.",
                ]}
              />
            </Section>

            <Section number="04" title="Our lawful bases">
              <Text>Depending on the activity, we rely on:</Text>
              <BulletList
                items={[
                  "Contract, where processing is needed to provide an account, booking, coupon placement, payment or platform feature.",
                  "Legitimate interests, including operating, securing and improving the platform, preventing misuse and managing business records.",
                  "Legal obligation, where records must be kept or disclosed under applicable law.",
                  "Consent, where consent is requested for a particular optional purpose.",
                ]}
              />
            </Section>

            <Section number="05" title="Who we share information with">
              <Text>We may share relevant information with:</Text>
              <BulletList
                items={[
                  "Supabase, for database, storage, authentication and platform infrastructure.",
                  "Stripe, for payment processing, checkout and transaction management.",
                  "Printing, fulfilment and delivery suppliers where needed to produce and distribute coupon campaigns.",
                  "Professional advisers, insurers, regulators, courts or public authorities where reasonably necessary or legally required.",
                  "Service providers supporting hosting, security, communications and technical operations.",
                ]}
              />
              <Text mt={3}>
                We do not sell personal information.
              </Text>
            </Section>

            <Section number="06" title="International processing">
              <Text>
                Some service providers may process information outside the United
                Kingdom. Where this happens, we use the safeguards required by
                applicable data-protection law, such as recognised adequacy arrangements
                or approved contractual protections.
              </Text>
            </Section>

            <Section number="07" title="How long we keep information">
              <Text>
                We keep information only for as long as reasonably necessary for the
                purpose for which it was collected. Account information is generally
                kept while an account remains active. Booking, payment, invoice and
                accounting records may be retained for up to six years where required
                for tax, accounting, audit, dispute or legal purposes. Security logs,
                support records and QR activity may be kept for shorter or longer
                periods where reasonably necessary to protect the platform or resolve a
                claim.
              </Text>
            </Section>

            <Section number="08" title="Your data-protection rights">
              <Text>Depending on the circumstances, rights may include:</Text>
              <BulletList
                items={[
                  "Access to personal information we hold about you.",
                  "Correction of inaccurate or incomplete information.",
                  "Deletion of information where the law permits it.",
                  "Restriction of certain processing.",
                  "Objection to processing based on legitimate interests.",
                  "Data portability where applicable.",
                  "Withdrawal of consent where consent is the lawful basis.",
                ]}
              />
              <Text mt={3}>
                Requests can be sent to{" "}
                <Link href="mailto:support@postcode.coupons" color={BRAND_RED} fontWeight="bold">
                  support@postcode.coupons
                </Link>. We may need to verify identity before completing a request.
              </Text>
            </Section>

            <Section number="09" title="Security">
              <Text>
                We use technical and organisational measures intended to protect
                personal information, including authentication, access controls and
                platform monitoring. No online service can guarantee absolute security,
                so account holders must also protect their credentials and tell us
                promptly about suspected unauthorised access.
              </Text>
            </Section>

            <Section number="10" title="Children">
              <Text>
                Postcode.Coupons business accounts and paid coupon-placement services
                are not intended for children. We do not knowingly invite children to
                create business accounts or purchase placements.
              </Text>
            </Section>

            <Section number="11" title="Complaints and changes">
              <Text>
                Please contact us first if there is a concern about how personal
                information is handled. You may also have the right to raise a complaint
                with the UK data-protection regulator. We may update this policy when
                the platform, suppliers or legal requirements change. The latest version
                will be published on this page with its updated date.
              </Text>
            </Section>
          </VStack>
        </Container>
      </Box>
    </PublicLayout>
  );
}
